import { type ComputedRef, type Ref, ref, watch, computed } from "vue";
import { Services } from "@ca.vmedia.tv/services";
import { usePlayerStore } from "@/stores/Player";
import { BIFParser, Logger } from "@/utils";
import type { AxiosError } from 'axios';

interface UseBifImage {
    getBif: (url: string) => Promise<void>;
    convertSrcToBif: (value: string) => string;
    isBifEnabled: Ref<boolean>;
}

export const useBifImage = (currentTimePosition: ComputedRef<number>, hoveredTimePos: Ref<number>, isVod: ComputedRef<boolean>): UseBifImage => {
    const bifCDN: string = 'https://t-bif.cdn';
    const bifExtension: string = '.bif';

    const PlayerStoreModule = usePlayerStore();
    const {
        setBifImage,
    } = PlayerStoreModule;

    const bifData: Ref<ArrayBuffer | null> = ref(null);

    const isBifEnabled = ref(false);
    const bifParser = computed((): BIFParser | null  => bifData.value ? new BIFParser(bifData.value) : null);
    const timePosition = computed((): number => hoveredTimePos.value > 0 ? hoveredTimePos.value : currentTimePosition.value);

    const getBif = async (url: string): Promise<void> => {
        try {
            if (isVod.value) {
                const result = await Services.Instance.player.getBif(url);
                if (result instanceof ArrayBuffer) {
                    bifData.value = result;
                    return;
                }
                throw new Error("Failed to get BIF data");
            }
        } catch (error) {
            Logger.captureError(error as AxiosError | Error);
        }
    };

    const getBifImage = (sec: number): void => {
        const imageData = bifParser.value?.getImageDataAtSecond(sec) ?? null;
        setBifImage(imageData);
    };

    const convertSrcToBif = (value: string): string => {
        return value.replace(/https:\/\/.*?\.cdn/, bifCDN) // Replace the CDN URL with the BIF CDN URL
                    .replace(/\.(m3u8|mpd)$/, bifExtension); // Replace the file extension from .m3u8 or .mpd to .bif
    };

    watch(
        timePosition,
        (value: number) => {
            if (value && bifData.value && isVod.value && isBifEnabled.value) {
                getBifImage(value);
            }
        }
    );

    return {
        getBif,
        convertSrcToBif,
        isBifEnabled,
    };
};
